<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import actibidAPI from 'dashboard/api/integrations/actibid';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ActibidPageLayout from './ActibidPageLayout.vue';

const SKIP_EXTRA_KEYS = new Set([
  'kind',
  'id',
  'codigo',
  'title',
  'city',
  'neighborhood',
  'status',
  'gestion',
  'price',
  'area_m2',
  'image_url',
  'images',
  'public_url',
  'matricula',
  'numero_matricula',
  'fmi',
  'banos',
  'descripcion',
  'members',
  'tipo_grupo',
  'items',
  'operator',
  'department',
  'estrato',
  'habitaciones',
  'parqueaderos',
  'tipo',
  'destino',
  'area_lote',
  'area_privada',
  'appraisal',
  'ano_construccion',
  'estado_habitado',
  'canon',
  'administracion',
  'cuota_ph',
  'piso',
  'niveles',
  'parqueaderos_cubiertos',
  'frente',
  'fondo',
  'features',
  'location',
  'auction',
  'schedule',
  'contact',
  'unit',
  'members_total',
  'members_truncated',
  'principal',
]);

const FACT_KEYS = [
  ['codigo', 'CODE'],
  ['matricula', 'FMI'],
  ['operator', 'OPERATOR'],
  ['city', 'LOCATION'],
  ['department', 'DEPARTMENT'],
  ['status', 'STATUS'],
  ['gestion', 'GESTION'],
  ['area_m2', 'AREA'],
  ['area_privada', 'PRIVATE_AREA'],
  ['area_lote', 'LOT_AREA'],
  ['ano_construccion', 'YEAR_BUILT'],
  ['estado_habitado', 'OCCUPANCY'],
  ['canon', 'CANON'],
  ['administracion', 'ADMIN_FEE'],
  ['cuota_ph', 'PH_FEE'],
  ['piso', 'FLOOR'],
  ['niveles', 'LEVELS'],
  ['estrato', 'ESTRATO'],
  ['habitaciones', 'ROOMS'],
  ['banos', 'BATHS'],
  ['parqueaderos', 'PARKING'],
  ['parqueaderos_cubiertos', 'COVERED_PARKING'],
  ['frente', 'FRONT'],
  ['fondo', 'DEPTH'],
  ['tipo', 'PROPERTY_TYPE'],
  ['destino', 'DESTINATION'],
  ['tipo_grupo', 'GROUP_TYPE'],
  ['kind', 'KIND'],
];

const store = useStore();
const { t } = useI18n();

const query = ref('');
const isSearching = ref(false);
const isLoadingDetail = ref(false);
const items = ref([]);
const searched = ref(false);
const lookupKind = ref('');
const detail = ref(null);
const members = ref([]);
const membersTotal = ref(null);
const membersTruncated = ref(false);
const errorMessage = ref('');

const integration = useFunctionGetter('integrations/getIntegration', 'actibid');
const hasHook = computed(() => !!integration.value?.hooks?.length);
const isUnit = computed(() => lookupKind.value === 'unit');
const showList = computed(() => items.value.length > 1);
const hasResult = computed(
  () => !!detail.value && Object.keys(detail.value).length
);

const objectAt = (item, key) => {
  const value = item?.[key];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value
    : null;
};

const features = computed(() => {
  const list = detail.value?.features;
  return Array.isArray(list) ? list.filter(Boolean).map(String) : [];
});
const auction = computed(() => objectAt(detail.value, 'auction'));
const schedule = computed(() => objectAt(detail.value, 'schedule'));
const contact = computed(() => objectAt(detail.value, 'contact'));
const parentUnit = computed(() => objectAt(detail.value, 'unit'));
const stages = computed(() => {
  const list = schedule.value?.etapas;
  return Array.isArray(list) ? list.slice(0, 8) : [];
});
const hasContact = computed(() => {
  const person = contact.value;
  return !!(person && (person.name || person.email || person.phone));
});

const field = (item, ...keys) => {
  if (!item) return '';
  const found = keys.find(key => {
    const value = item[key];
    return value !== undefined && value !== null && value !== '';
  });
  return found ? item[found] : '';
};

const fmiText = item => field(item, 'matricula', 'numero_matricula', 'fmi');

const locationText = item => {
  return [field(item, 'city'), field(item, 'neighborhood')]
    .filter(Boolean)
    .join(' · ');
};

const formatPrice = value => {
  const amount = Number(value);
  if (Number.isFinite(amount)) {
    return Math.round(amount).toLocaleString('es-CO');
  }
  return value;
};

const parseDay = value => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

const startOfToday = () => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatDay = value => {
  const date = parseDay(value);
  if (!date) return value || '';
  return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
};

const formatClock = value => {
  const match = String(value || '').match(/^(\d{2}):(\d{2})/);
  if (!match) return value || '';
  const hour = Number(match[1]);
  const suffix = hour >= 12 ? 'p. m.' : 'a. m.';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${match[2]} ${suffix}`;
};

const BOGOTA = 'America/Bogota';

const isInstant = value => String(value || '').includes('T');

const formatBogotaStamp = value => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('es-CO', {
    timeZone: BOGOTA,
    day: 'numeric',
    month: 'short',
  });
  const time = date.toLocaleTimeString('es-CO', {
    timeZone: BOGOTA,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${day}, ${time}`;
};

const formatWhen = value => {
  if (!value) return '';
  if (isInstant(value)) return formatBogotaStamp(value) || String(value);
  return formatDay(value);
};

const formatRange = (start, end) => {
  const from = formatWhen(start);
  const to = formatWhen(end);
  if (from && to && from !== to) return `${from} – ${to}`;
  return from || to || '';
};

const formatClockRange = (start, end) => {
  const from = formatClock(start);
  const to = formatClock(end);
  if (from && to && from !== to) return `${from} – ${to}`;
  return from || to || '';
};

const stageState = stage => {
  const currentName = schedule.value?.etapa_actual;
  const start = parseDay(stage?.fecha_inicio);
  const end = parseDay(stage?.fecha_fin) || start;
  const today = startOfToday();
  const nameMatch = currentName && stage?.nombre === currentName;
  const inRange = start && end && today >= start && today <= end;
  if (nameMatch || inRange) return 'current';
  if (end && end < today) return 'done';
  if (stage?.estado === 'activo' && !start && !end) return 'current';
  return 'upcoming';
};

const stageLabel = stage => {
  const state = stageState(stage);
  if (state === 'current') {
    return t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.STAGE_CURRENT');
  }
  if (state === 'done') {
    return t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.STAGE_DONE');
  }
  return t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.STAGE_UPCOMING');
};

const stageCardClass = stage => {
  const base = 'flex flex-col gap-1 p-3 rounded-xl outline outline-1 min-w-0';
  const state = stageState(stage);
  if (state === 'current') return `${base} outline-n-amber-8 bg-n-amber-2`;
  if (state === 'done') return `${base} outline-n-container bg-n-alpha-1`;
  return `${base} outline-n-container bg-n-card`;
};

const gallery = item => {
  const images = item?.images;
  if (Array.isArray(images) && images.length) return images;
  const url = field(item, 'image_url');
  return url ? [url] : [];
};

const factValue = (item, key) => {
  if (key === 'matricula') return fmiText(item);
  if (key === 'city') return locationText(item);
  if (
    ['price', 'appraisal', 'canon', 'administracion', 'cuota_ph'].includes(key)
  ) {
    const amount = field(item, key);
    return amount === '' ? '' : formatPrice(amount);
  }
  if (['area_m2', 'area_lote', 'area_privada'].includes(key)) {
    const area = field(item, key);
    if (area === '' || (key === 'area_lote' && Number(area) === 0)) return '';
    return t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.AREA_VALUE', { area });
  }
  return field(item, key);
};

const moneyAmount = (item, key) => {
  const raw = field(item, key);
  if (raw === '') return null;
  const amount = Number(raw);
  return Number.isFinite(amount) ? Math.round(amount) : null;
};

const priceFacts = item => {
  const price = moneyAmount(item, 'price');
  const base = moneyAmount(item, 'appraisal');
  if (price == null && base == null) return [];
  if (price != null && (base == null || price === base)) {
    return [
      {
        key: 'price',
        label: t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.PRICE'),
        value: formatPrice(price),
      },
    ];
  }
  if (base != null && price == null) {
    return [
      {
        key: 'appraisal',
        label: t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.BASE_PRICE'),
        value: formatPrice(base),
      },
    ];
  }
  return [
    {
      key: 'appraisal',
      label: t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.BASE_PRICE'),
      value: formatPrice(base),
    },
    {
      key: 'price',
      label: t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.PRICE_DISCOUNT'),
      value: formatPrice(price),
    },
  ];
};

const facts = item => {
  const areaIndex = FACT_KEYS.findIndex(([key]) => key === 'area_m2');
  const rows = FACT_KEYS.flatMap(([key, i18nKey], index) => {
    const priced = index === areaIndex ? priceFacts(item) : [];
    const value = factValue(item, key);
    if (value === '' || value === undefined || value === null) return priced;
    return [
      ...priced,
      {
        key,
        label: t(`INTEGRATION_SETTINGS.ACTIBID.INVENTORY.${i18nKey}`),
        value,
      },
    ];
  });

  const zone = objectAt(item, 'location')?.text;
  if (zone) {
    rows.push({
      key: 'location_text',
      label: t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.LOCATION_KIND'),
      value: zone,
    });
  }
  return rows;
};

const HIGHLIGHT_KEYS = ['status', 'operator', 'appraisal', 'price'];

const detailFacts = computed(() => facts(detail.value));
const leadFacts = computed(() =>
  HIGHLIGHT_KEYS.map(key =>
    detailFacts.value.find(row => row.key === key)
  ).filter(Boolean)
);
const bodyFacts = computed(() =>
  detailFacts.value.filter(row => !HIGHLIGHT_KEYS.includes(row.key))
);

const extraFacts = item => {
  if (!item) return [];
  return Object.entries(item)
    .filter(([key, value]) => {
      if (SKIP_EXTRA_KEYS.has(key)) return false;
      if (value === null || value === undefined || value === '') return false;
      return typeof value !== 'object';
    })
    .map(([key, value]) => ({ key, label: key, value: String(value) }));
};

const extractError = error => {
  const status = error?.response?.status;
  if (status === 404) {
    return t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.NOT_CONFIGURED');
  }
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    t('INTEGRATION_SETTINGS.ACTIBID.ALERTS.ERROR')
  );
};

const applyLookup = payload => {
  items.value = payload.items || [];
  lookupKind.value = payload.kind || '';
  detail.value =
    payload.item && Object.keys(payload.item).length ? payload.item : null;
  members.value = payload.members || [];
  membersTotal.value = payload.members_total ?? null;
  membersTruncated.value = payload.members_truncated === true;
};

const handleSearch = async () => {
  const q = query.value.trim();
  if (!q) {
    useAlert(t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.QUERY_REQUIRED'));
    return;
  }

  isSearching.value = true;
  errorMessage.value = '';
  detail.value = null;
  members.value = [];
  membersTotal.value = null;
  membersTruncated.value = false;
  try {
    const { data } = await actibidAPI.lookup(q);
    applyLookup(data.data || {});
    searched.value = true;
  } catch (error) {
    items.value = [];
    detail.value = null;
    members.value = [];
    membersTotal.value = null;
    membersTruncated.value = false;
    searched.value = true;
    errorMessage.value = extractError(error);
    useAlert(errorMessage.value);
  } finally {
    isSearching.value = false;
  }
};

const handleSelect = async item => {
  const q = field(item, 'codigo');
  if (!q) {
    lookupKind.value = field(item, 'kind') || 'property';
    detail.value = item;
    members.value = [];
    membersTotal.value = null;
    membersTruncated.value = false;
    return;
  }

  isLoadingDetail.value = true;
  errorMessage.value = '';
  try {
    const { data } = await actibidAPI.lookup(q);
    applyLookup(data.data || {});
  } catch (error) {
    detail.value = item;
    members.value = [];
    membersTotal.value = null;
    membersTruncated.value = false;
    useAlert(extractError(error));
  } finally {
    isLoadingDetail.value = false;
  }
};

const handleOpenMember = async member => {
  const codigo = field(member, 'codigo');
  if (!codigo) return;

  isLoadingDetail.value = true;
  errorMessage.value = '';
  try {
    const { data } = await actibidAPI.getProperty(codigo);
    lookupKind.value = data.data?.kind || 'property';
    detail.value = data.data || member;
    members.value = [];
    membersTotal.value = null;
    membersTruncated.value = false;
  } catch (error) {
    useAlert(extractError(error));
  } finally {
    isLoadingDetail.value = false;
  }
};

const isSelected = item => {
  return field(detail.value, 'codigo', 'id') === field(item, 'codigo', 'id');
};

onMounted(() => {
  store.dispatch('integrations/get');
});
</script>

<template>
  <ActibidPageLayout
    :header-title="t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.TITLE')"
  >
    <template #action>
      <form
        v-if="hasHook"
        class="flex items-center gap-2"
        @submit.prevent="handleSearch"
      >
        <Input
          v-model="query"
          type="search"
          :placeholder="
            t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.QUERY_PLACEHOLDER')
          "
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          class="w-56 sm:w-72"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>
        <Button
          type="submit"
          teal
          size="sm"
          :label="t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.SEARCH')"
          :is-loading="isSearching"
        />
      </form>
    </template>

    <p
      v-if="!hasHook"
      class="p-4 text-body-main text-n-slate-11 outline outline-n-container outline-1 bg-n-card rounded-xl"
    >
      {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.NOT_CONFIGURED') }}
    </p>

    <p v-else-if="!searched" class="text-body-main text-n-slate-11">
      {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.DESCRIPTION') }}
    </p>

    <p v-if="errorMessage" class="text-body-main text-n-ruby-11">
      {{ errorMessage }}
    </p>

    <div v-if="hasHook" class="flex flex-col lg:flex-row gap-4">
      <section
        v-if="showList"
        class="lg:w-72 flex-shrink-0 flex flex-col gap-2"
      >
        <button
          v-for="item in items"
          :key="field(item, 'codigo', 'id', 'title')"
          type="button"
          class="text-left p-3 outline outline-1 rounded-xl bg-n-card flex gap-2"
          :class="isSelected(item) ? 'outline-n-brand' : 'outline-n-container'"
          @click="handleSelect(item)"
        >
          <img
            v-if="field(item, 'image_url')"
            :src="field(item, 'image_url')"
            :alt="field(item, 'title')"
            class="w-12 h-12 object-cover rounded-md flex-shrink-0 bg-n-alpha-3"
          />
          <div class="flex flex-col min-w-0">
            <span class="text-n-slate-12 text-sm font-medium truncate">
              {{ field(item, 'title', 'codigo') }}
            </span>
            <span class="text-n-slate-11 text-xs truncate">
              {{ field(item, 'codigo') }}
            </span>
          </div>
        </button>
      </section>

      <section class="flex-1 min-w-0 flex flex-col gap-4">
        <p
          v-if="searched && !isSearching && !hasResult && !items.length"
          class="text-body-main text-n-slate-11"
        >
          {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.EMPTY') }}
        </p>
        <p v-else-if="isLoadingDetail" class="text-body-main text-n-slate-11">
          {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.LOADING') }}
        </p>
        <p
          v-else-if="searched && !hasResult"
          class="text-body-main text-n-slate-11"
        >
          {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.SELECT') }}
        </p>

        <article
          v-if="hasResult"
          class="p-4 outline outline-n-container outline-1 bg-n-card rounded-xl flex flex-col gap-4"
        >
          <div class="flex flex-wrap gap-2">
            <img
              v-for="(src, index) in gallery(detail)"
              :key="`${src}-${index}`"
              :src="src"
              :alt="field(detail, 'title')"
              class="h-36 w-48 object-cover rounded-md bg-n-alpha-3"
            />
          </div>
          <div class="flex flex-col gap-1">
            <p v-if="isUnit" class="text-n-slate-11 text-xs uppercase">
              {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.UNIT') }}
            </p>
            <h2 class="text-heading-2 text-n-slate-12">
              {{ field(detail, 'title', 'codigo') }}
            </h2>
          </div>
          <dl v-if="leadFacts.length" class="grid grid-cols-2 gap-2 m-0">
            <div
              v-for="fact in leadFacts"
              :key="fact.key"
              class="flex flex-col gap-0.5 px-3 py-2 rounded-lg bg-n-alpha-2"
            >
              <dt class="text-xs uppercase text-n-slate-11">
                {{ fact.label }}
              </dt>
              <dd class="text-base font-medium text-n-slate-12">
                {{ fact.value }}
              </dd>
            </div>
          </dl>
          <dl class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-body-main m-0">
            <div v-for="fact in bodyFacts" :key="fact.key">
              <dt class="text-n-slate-11">{{ fact.label }}</dt>
              <dd class="text-n-slate-12">{{ fact.value }}</dd>
            </div>
            <div v-for="fact in extraFacts(detail)" :key="`extra-${fact.key}`">
              <dt class="text-n-slate-11">{{ fact.label }}</dt>
              <dd class="text-n-slate-12">{{ fact.value }}</dd>
            </div>
          </dl>
          <div
            v-if="field(detail, 'descripcion')"
            v-dompurify-html="field(detail, 'descripcion')"
            class="prose prose-bubble max-w-none text-n-slate-12"
          />
          <div v-if="features.length" class="flex flex-col gap-2">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.FEATURES') }}
            </h3>
            <ul class="flex flex-wrap gap-2 m-0 p-0 list-none">
              <li
                v-for="name in features"
                :key="name"
                class="px-2 py-1 text-sm text-n-slate-12 bg-n-alpha-2 rounded-md"
              >
                {{ name }}
              </li>
            </ul>
          </div>
          <section v-if="auction" class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.AUCTION') }}
            </h3>
            <p v-if="auction.estado" class="text-body-main text-n-slate-12">
              {{ auction.estado }}
            </p>
            <p
              v-if="formatRange(auction.fecha_inicio, auction.fecha_fin)"
              class="text-base font-medium text-n-slate-12"
            >
              {{ formatRange(auction.fecha_inicio, auction.fecha_fin) }}
            </p>
          </section>
          <section v-if="schedule" class="flex flex-col gap-3">
            <div class="flex flex-col gap-1">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.SCHEDULE') }}
              </h3>
              <p v-if="schedule.nombre" class="text-body-main text-n-slate-12">
                {{ schedule.nombre }}
              </p>
              <p
                v-if="formatRange(schedule.fecha_inicio, schedule.fecha_fin)"
                class="text-heading-3 text-n-slate-12"
              >
                {{ formatRange(schedule.fecha_inicio, schedule.fecha_fin) }}
              </p>
              <p
                v-if="schedule.etapa_actual"
                class="text-body-main text-n-slate-12"
              >
                {{ schedule.etapa_actual }}
              </p>
            </div>
            <ul
              v-if="stages.length"
              class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 m-0 p-0 list-none"
            >
              <li
                v-for="stage in stages"
                :key="stage.id || stage.nombre"
                :class="stageCardClass(stage)"
              >
                <span
                  class="text-xs uppercase"
                  :class="
                    stageState(stage) === 'current'
                      ? 'text-n-amber-11'
                      : 'text-n-slate-11'
                  "
                >
                  {{ stageLabel(stage) }}
                </span>
                <span
                  v-if="formatRange(stage.fecha_inicio, stage.fecha_fin)"
                  class="text-base font-medium text-n-slate-12"
                >
                  {{ formatRange(stage.fecha_inicio, stage.fecha_fin) }}
                </span>
                <span class="text-sm text-n-slate-12">{{ stage.nombre }}</span>
                <span
                  v-if="formatClockRange(stage.hora_inicio, stage.hora_fin)"
                  class="text-n-slate-11 text-xs"
                >
                  {{ formatClockRange(stage.hora_inicio, stage.hora_fin) }}
                </span>
              </li>
            </ul>
          </section>
          <section v-if="hasContact" class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.CONTACT') }}
            </h3>
            <p v-if="contact.name" class="text-body-main text-n-slate-12">
              {{ contact.name }}
            </p>
            <a
              v-if="contact.email"
              :href="`mailto:${contact.email}`"
              class="text-n-brand text-sm"
            >
              {{ contact.email }}
            </a>
            <a
              v-if="contact.phone"
              :href="`tel:${contact.phone}`"
              class="text-n-brand text-sm"
            >
              {{ contact.phone }}
            </a>
          </section>
          <section v-if="parentUnit" class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.PARENT_UNIT') }}
            </h3>
            <a
              v-if="parentUnit.public_url"
              :href="parentUnit.public_url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-n-brand text-sm"
            >
              {{ field(parentUnit, 'title', 'codigo') }}
            </a>
            <p v-else class="text-body-main text-n-slate-12">
              {{ field(parentUnit, 'title', 'codigo') }}
            </p>
          </section>
          <a
            v-if="field(detail, 'public_url')"
            :href="field(detail, 'public_url')"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-brand text-sm"
          >
            {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.OPEN_PUBLIC') }}
          </a>
        </article>

        <div v-if="isUnit && members.length" class="flex flex-col gap-3">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.MEMBERS') }}
          </h3>
          <p v-if="membersTruncated" class="text-body-main text-n-slate-11">
            {{
              t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.MEMBERS_TRUNCATED', {
                shown: members.length,
                total: membersTotal || members.length,
              })
            }}
          </p>
          <div class="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <article
              v-for="member in members"
              :key="field(member, 'codigo', 'id')"
              class="p-4 outline outline-n-container outline-1 bg-n-card rounded-xl flex flex-col gap-2"
            >
              <button
                type="button"
                class="text-left flex flex-col gap-1"
                @click="handleOpenMember(member)"
              >
                <span
                  v-if="member.principal"
                  class="text-n-slate-11 text-xs uppercase"
                >
                  {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.PRINCIPAL') }}
                </span>
                <span class="text-n-slate-12 font-medium">
                  {{ field(member, 'title', 'codigo') }}
                </span>
                <span class="text-n-slate-11 text-sm">
                  {{ field(member, 'codigo') }}
                </span>
                <span
                  v-if="field(member, 'city')"
                  class="text-n-slate-11 text-sm"
                >
                  {{ field(member, 'city') }}
                </span>
                <span
                  v-if="field(member, 'price') !== ''"
                  class="text-n-slate-12 text-sm"
                >
                  {{ formatPrice(field(member, 'price')) }}
                </span>
              </button>
              <a
                v-if="field(member, 'public_url')"
                :href="field(member, 'public_url')"
                target="_blank"
                rel="noopener noreferrer"
                class="text-n-brand text-sm"
              >
                {{ t('INTEGRATION_SETTINGS.ACTIBID.INVENTORY.OPEN_PUBLIC') }}
              </a>
            </article>
          </div>
        </div>
      </section>
    </div>
  </ActibidPageLayout>
</template>
